import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import PageHeader from '../../../components/PageHeader';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useModuleLicense } from '../../../hooks/useModuleLicense';
import pbrService from '../../../services/pbr';
import cultivarService from '../../../services/cultivar';
import {
  pbrApplicationCreateSchema,
  type PbrApplicationCreateData,
  type Cultivar,
  type QualifiedPerson,
} from '../../../types/cultivar';
import { FileCheck, ShieldAlert } from 'lucide-react';

export default function PbrApplicationCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledCultivarId = searchParams.get('cultivar_id')
    ? Number(searchParams.get('cultivar_id'))
    : null;

  const { hasAccess, loading: licLoading } = useModuleLicense('cultivar');
  const [cultivars, setCultivars] = useState<Cultivar[]>([]);
  const [qps, setQps] = useState<QualifiedPerson[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<PbrApplicationCreateData>({
    resolver: zodResolver(pbrApplicationCreateSchema),
    defaultValues: {
      cultivar_id: prefilledCultivarId || undefined,
      jurisdiction: 'AU',
      application_type: 'pbr',
      upov_description_available: false,
    },
  });

  useEffect(() => {
    if (!hasAccess) return;
    cultivarService.searchCultivars({ limit: 500 }).then(r => {
      if (r.success && r.data) setCultivars(r.data.cultivars);
    });
    pbrService.listQualifiedPersons({ activeOnly: true }).then(r => {
      if (r.success && r.data) setQps(r.data);
    });
  }, [hasAccess]);

  if (licLoading) return <div className="p-6 text-neutral-500">Loading…</div>;
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: PbrApplicationCreateData) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await pbrService.createApplication(data);
      if (res.success && res.data) {
        navigate(`/cultivar/pbr/applications/${res.data.id}`);
      } else {
        setError(res.message || 'Failed to create');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="New PBR Application"
        icon={<FileCheck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-white border border-neutral-200 rounded-xl p-6 space-y-6"
        >
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1">Cultivar *</label>
            <select
              {...register('cultivar_id', { valueAsNumber: true })}
              className="input-professional w-full"
            >
              <option value="">Select…</option>
              {cultivars.map(c => (
                <option key={c.id} value={c.id}>
                  {c.trade_name || c.name}{c.botanical_name ? ` — ${c.botanical_name}` : ''}
                </option>
              ))}
            </select>
            {errors.cultivar_id && <p className="text-xs text-red-600 mt-1">{errors.cultivar_id.message}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">Jurisdiction *</label>
              <select {...register('jurisdiction')} className="input-professional w-full">
                <option value="AU">Australia (IP Australia)</option>
                <option value="US">United States (USPTO)</option>
                <option value="EU">European Union (CPVO)</option>
                <option value="NZ">New Zealand (PVR)</option>
                <option value="INT">International (UPOV)</option>
                <option value="JP">Japan</option>
                <option value="KR">South Korea</option>
                <option value="ZA">South Africa</option>
                <option value="CL">Chile</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1">Type *</label>
              <select {...register('application_type')} className="input-professional w-full">
                <option value="pbr">PBR</option>
                <option value="plant_patent">Plant Patent</option>
                <option value="cpvo">CPVO</option>
                <option value="pvr">PVR</option>
              </select>
            </div>
          </div>

          <h3 className="text-sm font-semibold text-neutral-700 pt-4 border-t border-neutral-100">Applicant</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input label="Applicant name" {...register('applicant_name')} error={errors.applicant_name?.message} />
            <Input label="Applicant country" {...register('applicant_country')} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-1"><Input label="State" {...register('applicant_state')} /></div>
            <div className="md:col-span-1"><Input label="Postcode" {...register('applicant_postcode')} /></div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1">Address</label>
            <textarea {...register('applicant_address')} rows={2} className="input-professional w-full" />
          </div>

          <h3 className="text-sm font-semibold text-neutral-700 pt-4 border-t border-neutral-100">Agent</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input label="Agent name" {...register('agent_name')} />
            <Input label="Contact person" {...register('agent_contact_person')} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input label="Phone" {...register('agent_phone')} />
            <Input label="Email" type="email" {...register('agent_email')} error={errors.agent_email?.message} />
          </div>

          <h3 className="text-sm font-semibold text-neutral-700 pt-4 border-t border-neutral-100">Qualified Person</h3>
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1">Nominated QP</label>
            <select
              {...register('qualified_person_id', {
                setValueAs: v => v === '' || v === null || v === undefined ? null : Number(v),
              })}
              className="input-professional w-full"
            >
              <option value="">— None selected —</option>
              {qps.map(qp => (
                <option key={qp.id} value={qp.id}>
                  {qp.full_name}{qp.accreditation_number ? ` (${qp.accreditation_number})` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-neutral-500 mt-1">
              Manage QPs in the{' '}
              <a href="/cultivar/pbr/qualified-persons" className="text-primary-600 hover:underline">QP register</a>.
            </p>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>
            <Button type="submit" disabled={submitting}>{submitting ? 'Creating…' : 'Create application'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
